import { ProjectTypes } from '@platform/types';
import { LngLatLike, Map, MapGeoJSONFeature, Point2D } from 'maplibre-gl';
import React, { useMemo } from 'react';

import { Hooks, Utils } from '@platform/maplibre';
import useDataSourceLayers from '../../../../hooks/useDataSourceLayers';
import { columnFormatter } from '../../../Datasets/Dataset/DatasetTable/utils';
import WorkingLayerTooltip, { TooltipContentItem } from '../WorkingLayerTooltip';
import WorkingLayerTooltipPositioner from '../WorkingLayerTooltipPositioner';

const { useFeaturesUnderMouse } = Hooks;

interface Props {
  map: Map | null;
  enabled: boolean;
  workingLayer: ProjectTypes.SocialExplorerWorkingLayer;
  features: MapGeoJSONFeature[];
  latLong: LngLatLike | null;
  onFeaturesUnderMouse: (wId: string, f: MapGeoJSONFeature[], latLng: LngLatLike | null) => void;
  activeFeature: MapGeoJSONFeature | null;
  actions?: React.ReactNode;
  inEditMode: boolean;
  onClose?: () => void;
  onFeatureSelectionChange?: (wId: string, fId: string, fName: string, fMeta?: object) => void;
}

const SocialExplorerWorkingLayerTooltipWrapper = ({
  map,
  onFeaturesUnderMouse,
  features,
  latLong,
  workingLayer,
  activeFeature,
  actions,
  onClose,
  enabled,
}: Props) => {
  const tooltip = workingLayer.tooltip as ProjectTypes.WorkingLayerTooltip;
  const workingLayerHash = Utils.getWorkingLayerId(workingLayer);
  const workingLayerIdAsArray = useMemo(() => (enabled ? [workingLayerHash] : []), [enabled, workingLayerHash]);

  useFeaturesUnderMouse(
    map,
    tooltip.action,
    (featuresInEvent: MapGeoJSONFeature[], latLong) => onFeaturesUnderMouse(workingLayer.id, featuresInEvent, latLong),
    workingLayerIdAsArray
  );

  const dataSourceLayersQuery = useDataSourceLayers(
    workingLayer.metadata.surveyName,
    workingLayer.metadata.isUserSurvey
  );

  const currentDataSourceLayer = dataSourceLayersQuery.data?.find(
    (x) => x['geo-layer-id'] === workingLayer.metadata.geoLayerId
  );

  if (!currentDataSourceLayer) return null;
  if (!activeFeature) return null;
  const activeFeatureIndex = features.indexOf(activeFeature);

  if (activeFeatureIndex === -1) return null;
  if (!latLong) return null;

  const projectedAnchor =
    activeFeature.geometry.type === 'Point'
      ? (map?.project(activeFeature.geometry.coordinates as LngLatLike) as Point2D)
      : (map?.project(latLong) as Point2D);

  const activeFeatureData = features[activeFeatureIndex].properties;

  let title =
    activeFeatureData && workingLayer.metadata.identifiers.name
      ? activeFeatureData[workingLayer.metadata.identifiers.name.variableId]
      : undefined;
  let content: TooltipContentItem[] = [];

  if (tooltip.enabled) {
    if (tooltip.title) {
      title = activeFeatureData?.[tooltip.title.variableId];
    }

    content = tooltip.content.map((x) => {
      const rawValue = activeFeatureData[x.variableId];
      let value, comparedToValue, comparedToTitle, secondaryValue;

      if (x.comparisonBase) {
        const comparedToRawValue = parseFloat(activeFeatureData[x.comparisonBase.base.variableId]);
        const calculatedPct = (100 * parseFloat(rawValue)) / comparedToRawValue; // the actual pct
        value = columnFormatter(calculatedPct, x.format);
        secondaryValue = columnFormatter(rawValue, x.comparisonBase.base.format); // store the actual count
        comparedToValue = columnFormatter(comparedToRawValue, x.comparisonBase.base.format);
        comparedToTitle = x.comparisonBase.base.label;
      } else {
        value = columnFormatter(rawValue, x.format);
      }

      return {
        id: x.id,
        title: x.label ?? x.variableId,
        value: value?.toString() || '',
        secondaryValue: secondaryValue?.toString() || '',
        comparedTo: x.comparisonBase
          ? {
              title: comparedToTitle ?? '',
              value: comparedToValue?.toString() || '',
            }
          : undefined,
      };
    });
  }

  // const featureId = activeFeatureData[workingLayer.metadata.identifiers.row.variableId];

  // const handleMaskSelectionToggle = () => {
  //   if (!activeFeature) return;
  //   if (!onMaskSelectionChange) return;
  //
  //   const featureName =
  //     activeFeatureData[
  //       workingLayer.metadata.identifiers.name?.variableId ?? workingLayer.metadata.identifiers.row.variableId
  //     ];
  //   onMaskSelectionChange(workingLayer.id, featureId, featureName);
  // };

  // const handleFeatureSelectionToggle = () => {
  //   if (!activeFeature) return;
  //   if (!onFeatureSelectionChange) return;
  //   const featureName =
  //     activeFeatureData[
  //       workingLayer.metadata.identifiers.name?.variableId ?? workingLayer.metadata.identifiers.row.variableId
  //     ];
  //   const featureMeta = { summaryLevelId: currentDataSourceLayer['summary-level'].id };
  //   onFeatureSelectionChange(workingLayer.id, featureId, featureName, featureMeta);
  // };

  // const activeFeatureIsInSelection = workingLayer.featureSelection.find((f) => f.id === featureId);

  // const selectionButton = onFeatureSelectionChange ? (
  //   <Buttons.Secondary
  //     onClick={handleFeatureSelectionToggle}
  //     className="bg-gray-200 text-gray-700 ring-0 hover:bg-gray-100"
  //   >
  //     {activeFeatureIsInSelection ? 'Deselect' : 'Add to selection'}
  //   </Buttons.Secondary>
  // ) : null;

  // const maskingButton = onFeatureSelectionChange ? (
  //   <Buttons.Secondary
  //     onClick={handleMaskSelectionToggle}
  //     className="bg-gray-200 text-gray-700 ring-0 hover:bg-gray-100"
  //   >
  //     {activeFeatureIsInSelection ? 'Remove from mask' : 'Add to mask'}
  //   </Buttons.Secondary>
  // ) : null;

  // const extendedActions = (
  //   <>
  //     {/*{selectionButton}*/}
  //     {/*{maskingButton}*/}
  //     {actions}
  //   </>
  // );

  return (
    <WorkingLayerTooltipPositioner anchorPoint={projectedAnchor} showAnchor={activeFeature.geometry.type !== 'Point'}>
      <WorkingLayerTooltip
        title={title}
        heading={workingLayer.name}
        data={content}
        footer={actions}
        onClose={onClose}
        emptyPlaceholder={null}
      />
    </WorkingLayerTooltipPositioner>
  );
};

export default SocialExplorerWorkingLayerTooltipWrapper;
